import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import DataTable from '../components/DataTable';
import { publicFetch } from '../lib/api';
import { toast } from 'sonner';

type ChallengeRow = {
  id: string;
  name: string;
  description: string;
  type: string;
  categories: string;
  active: string;
  liveAt: string;
  updatedAt: string;
  createdAt: string;
};

function mapChallenge(c: Record<string, unknown>): ChallengeRow {
  return {
    id: String(c.ChallengeId),
    name: String(c.Name ?? ''),
    description: String(c.Description ?? ''),
    type: String(c.ChallengeType ?? ''),
    categories: Array.isArray(c.Categories) ? c.Categories.join(', ') : '',
    active: String(c.IsActive ?? ''),
    liveAt: String(c.LiveAt ?? ''),
    updatedAt: String(c.UpdatedAt ?? ''),
    createdAt: String(c.CreatedAt ?? ''),
  };
}

export default function ChallengesPage() {
  const navigate = useNavigate();

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['challenges'],
    queryFn: async () => {
      const res = await publicFetch('/challenge');
      if (!res.ok) throw new Error(String(res.status));
      const json: Record<string, unknown>[] = await res.json();
      return json
        .sort(
          (a, b) =>
            Date.parse(String(b.UpdatedAt)) - Date.parse(String(a.UpdatedAt)),
        )
        .map(mapChallenge);
    },
  });

  useEffect(() => {
    if (error) toast.error('Could not load challenges.');
  }, [error]);

  const columns: ColumnDef<ChallengeRow, string>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'description', header: 'Description' },
    { accessorKey: 'type', header: 'Type' },
    { accessorKey: 'categories', header: 'Categories' },
    { accessorKey: 'active', header: 'Active' },
    { accessorKey: 'liveAt', header: 'Live at' },
    { accessorKey: 'updatedAt', header: 'Updated' },
    { accessorKey: 'id', header: 'ID' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Challenges</h1>
          <p className="text-sm text-ink-muted">
            Click a row to open the editor. List loads from{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">GET /challenge</code>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/challenges/create')}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700"
        >
          New challenge
        </button>
      </div>
      {isLoading ? (
        <p className="text-ink-muted">Loading…</p>
      ) : (
        <DataTable
          data={data}
          columns={columns}
          searchPlaceholder="Filter challenges…"
          onRowClick={(row) => navigate(`/challenges/create/${row.id}`)}
        />
      )}
    </div>
  );
}
